import React, { DetailedHTMLProps, ImgHTMLAttributes, useEffect, useState } from 'react';
import { useIsOnScreen } from '../useIsOnScreen/useIsOnScreen';
import { classNames } from '@jbhi-fi/lanyard-ui/global/utils';

type ImgProps = DetailedHTMLProps<ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>;

export type UseProgressiveImage = ((
  props: {
    placeholderSrc: string;
    transitionDuration?: React.CSSProperties['transitionDuration'];
  } & ImgProps
) => ImgProps) & { isEnabled: boolean };

export const useProgressiveImage: UseProgressiveImage = ({
  src,
  className,
  srcSet,
  sizes,
  placeholderSrc,
  style,
  transitionDuration = '200ms',
  ...props
}) => {
  const imageRef = React.createRef<HTMLImageElement>();
  const hasBeenOnScreen = useIsOnScreen(imageRef, { persistOnceSeen: true, threshold: 0.1 });
  const [isLoading, setIsLoading] = useState<boolean>(useProgressiveImage.isEnabled);

  useEffect(() => {
    let shouldCancel = false;
    if (!hasBeenOnScreen || !imageRef.current || !useProgressiveImage.isEnabled) return;

    const image = new Image(imageRef.current.clientWidth, imageRef.current.clientHeight);
    image.onload = () => !shouldCancel && setIsLoading(false);
    image.onerror = () => !shouldCancel && setIsLoading(false);
    image.src = src || '';
    image.srcset = srcSet || '';
    image.sizes = sizes || '';
    if (image.complete) setIsLoading(false);
    return () => {
      shouldCancel = true;
    };
  }, [hasBeenOnScreen]);

  return {
    ...props,
    ref: imageRef,
    className: classNames(['chromatic-ignore', className]),
    sizes,
    style: {
      /* webpage freezes when using classNames & useStyles(), so using style prop instead. */
      transition: `filter linear ${transitionDuration}`,
      filter: isLoading ? 'blur(5px)' : undefined,
      ...style,
    },
    ...(isLoading ? { src: placeholderSrc } : { src, srcSet }),
  };
};

useProgressiveImage.isEnabled = true;

export type ProgressiveImageProps = Parameters<UseProgressiveImage>[0];
export const ProgressiveImage = (props: ProgressiveImageProps) => {
  const imgProps = useProgressiveImage(props);

  return <img {...imgProps} alt={imgProps.alt} />;
};
